import { useState, useRef, useEffect, useCallback } from "react";
import classnames from "classnames";
import { SOLUTION_GALERY_ITEM_CLICKED, SOLUTION_GALERY_VIDEO_INTERACTION } from "constants/bigbrain-event-types";
import { trackEvent } from "services/bigbrain-service";

import { componentStyles } from "./asset-gallery.scss";

export const ASSET_TYPE_IMAGE = "image";
export const ASSET_TYPE_VIDEO = "video";

const VIDEO_PLAY = "video_play";
const VIDEO_PAUSE = "video_pause";
const VIDEO_ENDED = "video_ended";

const PLAY_VIDEO_IMG_SRC =
  "https://dapulse-res.cloudinary.com/image/upload/mondayhomepage/template-store/template-page/play.png";

const AssetGallery = ({ assets }) => {
  const [isScrolledUntilBottom, setIsScrolledUntilBottom] = useState(false);
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);
  const sideGalleryRef = useRef();

  useEffect(() => {
    const handleScroll = () => {
      const { offsetHeight, scrollTop, scrollHeight } = sideGalleryRef.current;
      const isScrolledToBottom = offsetHeight + scrollTop >= scrollHeight;
      setIsScrolledUntilBottom(isScrolledToBottom);
    };

    sideGalleryRef.current?.addEventListener("scroll", handleScroll);

    return () => sideGalleryRef.current?.removeEventListener("scroll", handleScroll);
  }, [isScrolledUntilBottom]);

  const selectAsset = useCallback(
    (index) => {
      trackEvent(SOLUTION_GALERY_ITEM_CLICKED, { kind: assets[index]?.type, info1: index, info2: assets.length });
      setSelectedAssetIndex(index);
    },
    [assets]
  );

  /*
    Renderers
  */

  const renderSideImagesOverlayIfNeeded = useCallback(() => {
    if (isScrolledUntilBottom) return null;

    return <div className="preview-side-images-bottom-overlay" />;
  }, [isScrolledUntilBottom]);

  const renderPlayIcon = () => {
    return <img className="video-thumbnail-icon" src={PLAY_VIDEO_IMG_SRC} />;
  };

  const onVideoInteraction = (asset, actionType) => {
    trackEvent(SOLUTION_GALERY_VIDEO_INTERACTION, { kind: actionType, info1: asset.src });
  };

  const renderAssetIndex = useCallback(
    (assetIndex, isMain = false) => {
      const className = isMain ? "main-asset" : "preview-asset";
      const asset = assets[assetIndex];

      if (asset.type === ASSET_TYPE_VIDEO) {
        return (
          <>
            <video
              className={className}
              src={asset.src}
              controls={isMain}
              onPlay={() => onVideoInteraction(asset, VIDEO_PLAY)}
              onEnded={() => onVideoInteraction(asset, VIDEO_ENDED)}
              onPause={() => onVideoInteraction(asset, VIDEO_PAUSE)}
            />
            {!isMain && renderPlayIcon()}
          </>
        );
      }
      return <img className={className} src={asset.src} alt={asset.title} />;
    },
    [assets]
  );

  const renderPreviewAssets = useCallback(() => {
    return assets.map((asset, index) => {
      return (
        <div
          className={classnames("preview-asset-wrapper", { selected: index === selectedAssetIndex })}
          key={index}
          onClick={() => selectAsset(index)}
        >
          {renderAssetIndex(index)}
        </div>
      );
    });
  }, [assets, selectedAssetIndex, selectAsset, renderAssetIndex]);

  const renderNewAssets = () => {
    return (
      <div className="asset-gallery">
        <div className="main-asset-wrapper">{renderAssetIndex(selectedAssetIndex, true)}</div>
        <div className="assets-previews-wrapper">
          <div className="previews-container" ref={sideGalleryRef}>
            {renderPreviewAssets()}
          </div>

          {renderSideImagesOverlayIfNeeded()}
        </div>
      </div>
    );
  };

  return (
    <div className="asset-gallery">
      {renderNewAssets()}
      <style jsx>{componentStyles}</style>
    </div>
  );
};

export default AssetGallery;
